import React from "react";
import { graphql } from "gatsby";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import Layout from "../components/layout";
import Seo from "../components/seo";
import ContactUs from "../components/contact-us";
import BlogSlider from "../components/blog-slider";
import Meta from "../components/meta";
import Breadcrumbs from "../components/breadcrumbs";
import BlockEmbeddedAsset from "../components/rich-text/block-embedded-asset";
import InlineEntryHyperlink from "../components/rich-text/inline-entry-hyperlink";
import InlineAssetHyperlink from "../components/rich-text/inline-asset-hyperlink";
import InlineHyperlinkEmbed from "../components/rich-text/inline-hyperlink-embed";
import withPreview from "../utility/with-preview";
import slugify from "../utility/slugify";
import Photogallery from "../components/photogallery";
import NewsletterTeaser from "../components/newsletter-teaser";
import { useTranslation } from "react-i18next";
import InlineScript from "../hooks/useInlineScript";
import { getDomain } from "../utility/language-utils";
import ContactUsCn from "../components/contact-us-cn";
import NewsletterTeaserCN from "../components/newsletter-teaser-cn";

const BlogDetailPage = ({ data, location }) => {
  const {
    bodytext,
    category,
    publicationDate,
    relatedPosts,
    subtitle,
    tags,
    title,
    introduction,
    gallery,
  } = data?.contentfulBlog;
  const { href } = location;
  const { t } = useTranslation();

  const jsonBodyContent = JSON.parse(bodytext?.raw);
  const heading = jsonBodyContent?.content.filter((item) => item.nodeType === "heading-2") || [];
  const jsonIntroContent = introduction?.raw ? JSON.parse(introduction?.raw) : null;

  const renderOptions = (references) => ({
    renderText: (text) => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    renderNode: {
      [INLINES.HYPERLINK]: (node, children) => (
        <InlineHyperlinkEmbed node={node}>{children}</InlineHyperlinkEmbed>
      ),
      [BLOCKS.HEADING_2]: (node, children) => {
        const content = node.content[0].value;
        return <h2 id={slugify(content)}>{children}</h2>;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const { id } = node.data.target.sys;
        const block = references.find((i) => i.contentful_id === id);
        const match = block?.buzzsproutUrl?.match(/container_id=buzzsprout-player-(\d+)/);
        const playerId = match ? match[1] : null;
        return playerId ? (
          <>
            <div id={`buzzsprout-player-${playerId}`}></div>
            <InlineScript url={block?.buzzsproutUrl} />
          </>
        ) : null;
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => <BlockEmbeddedAsset node={node} references={references} />,
      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <InlineEntryHyperlink node={node} references={references}>
          {children}
        </InlineEntryHyperlink>
      ),
      [INLINES.ASSET_HYPERLINK]: (node, children) => (
        <InlineAssetHyperlink node={node} references={references}>
          {children}
        </InlineAssetHyperlink>
      ),
    },
  });

  return (
    <Layout type="blog" location={location}>
      <main className="page pt-3">
        <article>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <Breadcrumbs
                  pathItems={[
                    {
                      url: "/",
                      label: t("home"),
                    },
                    {
                      url: "/insights",
                      label: t("insights"),
                    },
                    {
                      label: title,
                    },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="page__section">
            <div className="container">
              <div className="row">
                <h1 className="h2 page__title">{title}</h1>
                {subtitle && <div className="page__subtitle">{subtitle}</div>}
              </div>
            </div>
          </div>

          <Meta
            type="Blog"
            title={title}
            category={category}
            tags={tags}
            date={publicationDate}
            url={href}
          />

          <div className="page__section">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="page__content page__content--detail">
                    {introduction && (
                      <div className="page__intro">
                        {documentToReactComponents(
                          jsonIntroContent,
                          renderOptions(introduction.references)
                        )}
                      </div>
                    )}

                    {introduction && heading?.length > 0 && (
                      <div className="page__toc">
                        <ul>
                          {heading.map((item, index) => (
                            <li key={index}>
                              <a href={`#${slugify(item.content[0].value)}`}>
                                {item.content[0].value}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="page__text">
                      {documentToReactComponents(
                        jsonBodyContent,
                        renderOptions(bodytext.references)
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!!gallery?.length && (
            <div className="page__section pb-5 bgcolor--gray-light-mid">
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <Photogallery images={gallery} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </article>
        <section className="page__section page__section--pagination">
          {getDomain() === "cn" ? (
            <NewsletterTeaserCN id="teaser" newsTitle={t("stayInformed")} internal={true} />
          ) : (
            <NewsletterTeaser id="teaser" newsTitle={t("stayInformed")} internal={true} />
          )}
        </section>
        <section className="page__section page__section--pagination">
          {relatedPosts && <BlogSlider title={t("relatedPosts")} items={relatedPosts} />}
        </section>
      </main>

      {getDomain() === "cn" ? <ContactUsCn /> : <ContactUs />}
    </Layout>
  );
};

export const Head = ({ data, location, pageContext }) => {
  const { metaDescription, sharingImage, titleHtml, title } = data?.contentfulBlog;
  return (
    <Seo
      title={titleHtml || title}
      description={metaDescription?.metaDescription}
      ogImage={sharingImage}
      pathname={location.pathname}
      lang={pageContext.language}
    />
  );
};

export default withPreview(BlogDetailPage, { subField: "contentfulBlog" });

export const query = graphql`
  query ($contentful_id: String!, $language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      nodes {
        ns
        data
        language
      }
    }
    contentfulBlog(contentful_id: { eq: $contentful_id }, node_locale: { eq: $language }) {
      id
      contentful_id
      category
      link
      publicationDate
      sharingImage {
        height
        width
        file {
          url
        }
        relativeUrl
      }
      tags
      title
      relatedPosts {
        contentful_id
        link
        category
        tags
        title
        publicationDate
        sharingImage {
          gatsbyImageData(placeholder: NONE)
        }
        image {
          gatsbyImageData(width: 800, placeholder: NONE)
        }
      }
      introduction {
        raw
        references {
          ... on ContentfulAsset {
            __typename
            contentful_id
            mimeType
            description
            gatsbyImageData
            title
            file {
              url
            }
            relativeUrl
          }
        }
      }
      bodytext {
        raw
        references {
          ... on ContentfulAsset {
            __typename
            contentful_id
            mimeType
            description
            gatsbyImageData
            title
            file {
              url
            }
            relativeUrl
          }
          ... on ContentfulStaticComponent {
            contentful_id
            buzzsproutUrl
          }
          ... on ContentfulNews {
            __typename
            contentful_id
            link
          }
          ... on ContentfulBlog {
            __typename
            contentful_id
            link
          }
          ... on ContentfulPageOverview {
            contentful_id
            anchorTitle
            product_tabs {
              contentful_id
              layout_page {
                link
              }
            }
          }
          ... on ContentfulLayoutPage {
            __typename
            contentful_id
            link
          }
        }
      }
      gallery {
        ... on ContentfulAsset {
          __typename
          contentful_id
          mimeType
          description
          gatsbyImageDataSmall: gatsbyImageData(width: 400)
          gatsbyImageDataFull: gatsbyImageData(width: 1920)
          title
          file {
            url
          }
          relativeUrl
        }
      }
    }
  }
`;
